import { useFrame, useThree } from '@react-three/fiber'
import { animate, type Easing } from 'motion'
import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import {
    endCardTiltHorizontalNormal,
    endCardTiltHorizontalStele,
    endCardTiltSpeedNormal,
    endCardTiltSpeedStele,
    endCardTiltVerticalNormal,
    endCardTiltVerticalStele,
    lanePositions
} from '../config/constants'
import { store } from '../../stores/store'
import { useRunState } from '../provider/useRunState'
import { lerp } from 'three/src/math/MathUtils.js'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'

// minimun transition overlay time
const RESET_TRANSITION_MIN_MS = 500

type CameraPose = {
    key:
    | 'idle'
    | 'start'
    | 'intro'
    | 'configurator'
    | 'running'
    | 'gameOver'
    | 'completed'
    | 'endcard'
    rotatorPosition: THREE.Vector3
    rotatorRotation: THREE.Vector3
    cameraPosition: THREE.Vector3
    cameraLookAt: THREE.Vector3
    transitionDuration: number
    transitionEase: Easing
    fov: number
}

const CAMERA_POSE_START: CameraPose = {
    key: 'start',
    rotatorPosition: new THREE.Vector3(0.0, 0.0, 0.0),
    rotatorRotation: new THREE.Vector3(0.0, Math.PI - 0.4, 0.0),
    cameraPosition: new THREE.Vector3(1.0, 0.9, 4.8),
    cameraLookAt: new THREE.Vector3(-0.03, 0.6, 0.0),
    transitionDuration: 2.0,
    transitionEase: 'easeInOut',
    fov: 26
}

const CAMERA_POSE_START_STELE: CameraPose = {
    key: 'start',
    rotatorPosition: new THREE.Vector3(0.0, 0.0, 0.0),
    rotatorRotation: new THREE.Vector3(0.0, Math.PI - 0.4, 0.0),
    cameraPosition: new THREE.Vector3(1.1, 0.7, 5.0),
    cameraLookAt: new THREE.Vector3(-0.02, 0.36, 0.0),
    transitionDuration: 2.0,
    transitionEase: 'easeInOut',
    fov: 24
}

const CAMERA_POSE_CONFIGURATOR: CameraPose = {
    key: 'configurator',
    rotatorPosition: new THREE.Vector3(0.0, 0.0, 0.0),
    rotatorRotation: new THREE.Vector3(0.0, Math.PI, 0.0),
    cameraPosition: new THREE.Vector3(0.0, 0.9, 7.0),
    cameraLookAt: new THREE.Vector3(0.0, 0.2, 0.0),
    transitionDuration: 2.0,
    transitionEase: 'easeInOut',
    fov: 24
}

const CAMERA_POSE_CONFIGURATOR_STELE: CameraPose = {
    key: 'configurator',
    rotatorPosition: new THREE.Vector3(0.0, 0.0, 0.0),
    rotatorRotation: new THREE.Vector3(0.0, Math.PI, 0.0),
    cameraPosition: new THREE.Vector3(0.0, 0.8, 5),
    cameraLookAt: new THREE.Vector3(0.0, 0.16, 0.0),
    transitionDuration: 2.0,
    transitionEase: 'easeInOut',
    fov: 26
}

const CAMERA_POSE_INTRO: CameraPose = {
    key: 'intro',
    rotatorPosition: new THREE.Vector3(0.0, 0.0, 0.0),
    rotatorRotation: new THREE.Vector3(0.0, Math.PI, 0.0),
    cameraPosition: new THREE.Vector3(0.0, 0.9, 7.0),
    cameraLookAt: new THREE.Vector3(0.0, 0.2, 0.0),
    transitionDuration: 2.0,
    transitionEase: 'easeInOut',
    fov: 24
}

const CAMERA_POSE_INTRO_STELE: CameraPose = {
    key: 'intro',
    rotatorPosition: new THREE.Vector3(0.0, 0.0, 0.0),
    rotatorRotation: new THREE.Vector3(0.0, Math.PI, 0.0),
    cameraPosition: new THREE.Vector3(0.0, 0.8, 5),
    cameraLookAt: new THREE.Vector3(0.0, 0.16, 0.0),
    transitionDuration: 2.0,
    transitionEase: 'easeInOut',
    fov: 26
}

const CAMERA_POSE_RUNNING: CameraPose = {
    key: 'running',
    rotatorPosition: new THREE.Vector3(0, 0, 0),
    rotatorRotation: new THREE.Vector3(0, 0, 0),
    cameraPosition: new THREE.Vector3(0, 1.5, 3.0),
    cameraLookAt: new THREE.Vector3(0, 1, 0),
    transitionDuration: 5.0,
    transitionEase: 'easeInOut',
    fov: 60
}

const CAMERA_POSE_RUNNING_STELE: CameraPose = {
    key: 'running',
    rotatorPosition: new THREE.Vector3(0, 0, 0),
    rotatorRotation: new THREE.Vector3(0, 0, 0),
    cameraPosition: new THREE.Vector3(0, 1.5, 3.0),
    cameraLookAt: new THREE.Vector3(0, 1, 0),
    transitionDuration: 5.0,
    transitionEase: 'easeInOut',
    fov: 60
}

const CAMERA_POSE_GAMEOVER: CameraPose = {
    key: 'gameOver',
    rotatorPosition: new THREE.Vector3(0, 0, 0),
    rotatorRotation: new THREE.Vector3(0, Math.PI, 0),
    cameraPosition: new THREE.Vector3(-2.2, 0.8, 5),
    cameraLookAt: new THREE.Vector3(0.12, 0.5, 0),
    transitionDuration: 3.0,
    transitionEase: 'easeInOut',
    fov: 24
}

const CAMERA_POSE_GAMEOVER_STELE: CameraPose = {
    key: 'gameOver',
    rotatorPosition: new THREE.Vector3(0, 0, 0),
    rotatorRotation: new THREE.Vector3(0, Math.PI, 0),
    cameraPosition: new THREE.Vector3(-2.2, 0.8, 5),
    cameraLookAt: new THREE.Vector3(0.12, 0.6, 0),
    transitionDuration: 3.0,
    transitionEase: 'easeInOut',
    fov: 18
}

export const CAMERA_POSE_COMPLETED: CameraPose = {
    key: 'completed',
    rotatorPosition: new THREE.Vector3(0, 0.4, 0),
    rotatorRotation: new THREE.Vector3(0, Math.PI - 1.2, 0),
    cameraPosition: new THREE.Vector3(0, 0.4, 3.3),
    cameraLookAt: new THREE.Vector3(0, 0.4, 0),
    transitionDuration: 2.3,
    transitionEase: 'easeInOut',
    fov: 60
}

const CAMERA_POSE_COMPLETED_STELE: CameraPose = {
    key: 'completed',
    rotatorPosition: new THREE.Vector3(0, 0.4, 0),
    rotatorRotation: new THREE.Vector3(0, Math.PI - 1.2, 0),
    cameraPosition: new THREE.Vector3(0, 0.4, 3.3),
    cameraLookAt: new THREE.Vector3(0, 0.4, 0),
    transitionDuration: 2.3,
    transitionEase: 'easeInOut',
    fov: 60
}

const CAMERA_POSE_ENDCARD: CameraPose = {
    key: 'endcard',
    rotatorPosition: new THREE.Vector3(0, 4.1, 0),
    rotatorRotation: new THREE.Vector3(0, Math.PI + 1.0, 0),
    cameraPosition: new THREE.Vector3(0.0, 0.0, 4.25),
    cameraLookAt: new THREE.Vector3(0.0, 0.0, 0),
    transitionDuration: 2.0,
    transitionEase: 'easeOut',
    fov: 40
}

const CAMERA_POSE_ENDCARD_STELE: CameraPose = {
    key: 'endcard',
    rotatorPosition: new THREE.Vector3(0, 4.1, 0),
    rotatorRotation: new THREE.Vector3(0, Math.PI + 1.0, 0),
    cameraPosition: new THREE.Vector3(0.0, 0.0, 4.25),
    cameraLookAt: new THREE.Vector3(0.0, 0.0, 0),
    transitionDuration: 2.0,
    transitionEase: 'easeOut',
    fov: 40
}

const CAMERA_POSE_ENDCARD_PRESSED: CameraPose = {
    key: 'endcard',
    rotatorPosition: new THREE.Vector3(0, 4.0, 0),
    rotatorRotation: new THREE.Vector3(0, Math.PI + 1.0, 0),
    cameraPosition: new THREE.Vector3(0.0, -0.45, 5.3),
    cameraLookAt: new THREE.Vector3(0.0, -0.45, 0),
    transitionDuration: 2.0,
    transitionEase: 'easeOut',
    fov: 40
}

const CAMERA_POSE_ENDCARD_PRESSED_STELE: CameraPose = {
    key: 'endcard',
    rotatorPosition: new THREE.Vector3(0, 4.0, 0),
    rotatorRotation: new THREE.Vector3(0, Math.PI + 1.0, 0),
    cameraPosition: new THREE.Vector3(0.0, -0.8, 5.3),
    cameraLookAt: new THREE.Vector3(0.0, -0.8, 0),
    transitionDuration: 2.0,
    transitionEase: 'easeOut',
    fov: 40
}



export function CameraAnimator() {
    const runState = useRunState()
    const size = useThree((state) => state.size)
    const set = useThree((state) => state.set)
    const isStele = useSteleBreakpoint()
    const cameraPoseStart = isStele ? CAMERA_POSE_START_STELE : CAMERA_POSE_START
    const cameraPoseConfigurator = isStele ? CAMERA_POSE_CONFIGURATOR_STELE : CAMERA_POSE_CONFIGURATOR
    const cameraPoseIntro = isStele ? CAMERA_POSE_INTRO_STELE : CAMERA_POSE_INTRO
    const cameraPoseRunning = isStele ? CAMERA_POSE_RUNNING_STELE : CAMERA_POSE_RUNNING
    const cameraPoseGameOver = isStele ? CAMERA_POSE_GAMEOVER_STELE : CAMERA_POSE_GAMEOVER
    const cameraPoseCompleted = isStele ? CAMERA_POSE_COMPLETED_STELE : CAMERA_POSE_COMPLETED
    const cameraPoseEndCard = isStele ? CAMERA_POSE_ENDCARD_STELE : CAMERA_POSE_ENDCARD
    const cameraPoseEndCardPressed = isStele ? CAMERA_POSE_ENDCARD_PRESSED_STELE : CAMERA_POSE_ENDCARD_PRESSED
    const endCardTiltHorizontal = isStele ? endCardTiltHorizontalStele : endCardTiltHorizontalNormal
    const endCardTiltVertical = isStele ? endCardTiltVerticalStele : endCardTiltVerticalNormal
    const endCardTiltSpeed = isStele ? endCardTiltSpeedStele : endCardTiltSpeedNormal


    const cameraRef = useRef<THREE.PerspectiveCamera>(null)
    const cameraRootGroupRef = useRef<THREE.Group>(null)
    const cameraLineGroupRef = useRef<THREE.Group>(null)
    const cameraWiggleGroupRef = useRef<THREE.Group>(null)
    const cameraRotationGroupRef = useRef<THREE.Group>(null)
    const cameraPositionGroupRef = useRef<THREE.Group>(null)

    const currentCameraLookAtRef = useRef(cameraPoseStart.cameraLookAt.clone())
    const currentRotatorRotationRef = useRef(
        new THREE.Vector3(
            cameraPoseStart.rotatorRotation.x,
            cameraPoseStart.rotatorRotation.y,
            cameraPoseStart.rotatorRotation.z,
        ),
    )
    const transitionStartRef = useRef({
        key: cameraPoseStart.key,
        rotatorPosition: cameraPoseStart.rotatorPosition.clone(),
        rotatorRotation: cameraPoseStart.rotatorRotation.clone(),
        cameraPosition: cameraPoseStart.cameraPosition.clone(),
        cameraLookAt: cameraPoseStart.cameraLookAt.clone(),
        transitionDuration: cameraPoseStart.transitionDuration,
        transitionEase: cameraPoseStart.transitionEase,
        fov: cameraPoseStart.fov,
    })
    const activePoseRef = useRef<CameraPose>(cameraPoseStart)
    const activeAnimationRef = useRef<ReturnType<typeof animate> | null>(null)
    const endCardTiltTargetRef = useRef({ x: 0, y: 0 })
    const endCardTiltCurrentRef = useRef({ x: 0, y: 0 })
    const endCardUiInteractionRef = useRef(false)
    const previousAppFlowPhaseRef = useRef(store.appFlowPhase)

    // Camera Position and Target Setup
    useEffect(() => {
        const cameraPositionGroup = cameraPositionGroupRef.current
        const cameraRotationGroup = cameraRotationGroupRef.current
        const camera = cameraRef.current
        if (
            cameraPositionGroup == null ||
            cameraRotationGroup == null ||
            camera == null
        ) {
            return
        }

        activePoseRef.current = cameraPoseStart
        currentCameraLookAtRef.current.copy(cameraPoseStart.cameraLookAt)
        currentRotatorRotationRef.current.copy(cameraPoseStart.rotatorRotation)
        transitionStartRef.current.key = cameraPoseStart.key
        transitionStartRef.current.rotatorPosition.copy(cameraPoseStart.rotatorPosition)
        transitionStartRef.current.rotatorRotation.copy(cameraPoseStart.rotatorRotation)
        transitionStartRef.current.cameraPosition.copy(cameraPoseStart.cameraPosition)
        transitionStartRef.current.cameraLookAt.copy(cameraPoseStart.cameraLookAt)
        transitionStartRef.current.transitionDuration =
            cameraPoseStart.transitionDuration
        transitionStartRef.current.transitionEase = cameraPoseStart.transitionEase
        transitionStartRef.current.fov = cameraPoseStart.fov

        camera.fov = cameraPoseStart.fov
        camera.updateProjectionMatrix()
        cameraPositionGroup.position.copy(cameraPoseStart.cameraPosition)
        cameraPositionGroup.quaternion.copy(
            createLocalLookAtQuaternion(
                cameraPoseStart.cameraPosition,
                cameraPoseStart.cameraLookAt,
            ),
        )
        cameraRotationGroup.position.copy(cameraPoseStart.rotatorPosition)
        cameraRotationGroup.rotation.setFromVector3(cameraPoseStart.rotatorRotation)
    }, [cameraPoseStart, cameraPositionGroupRef, cameraRotationGroupRef, cameraRef])

    // Animation Setup
    useEffect(() => {
        return () => {
            activeAnimationRef.current?.stop()
            activeAnimationRef.current = null
        }
    }, [])

    // Camera active and screen resize setup
    useEffect(() => {
        if (cameraRef.current == null) {
            return
        }
        const camera = cameraRef.current
        set({ camera })
        camera.aspect = Math.max(size.width, 1) / Math.max(size.height, 1)
        camera.updateProjectionMatrix()
    }, [set, cameraRef, size.height, size.width])

    useEffect(() => {
        const previousAppFlowPhase = previousAppFlowPhaseRef.current
        const currentAppFlowPhase = store.appFlowPhase
        previousAppFlowPhaseRef.current = currentAppFlowPhase

        if (
            previousAppFlowPhase !== 'run' ||
            currentAppFlowPhase !== 'countdown'
        ) {
            return
        }

        const cameraPositionGroup = cameraPositionGroupRef.current
        const cameraRotationGroup = cameraRotationGroupRef.current
        const cameraLineGroup = cameraLineGroupRef.current
        const cameraWiggleGroup = cameraWiggleGroupRef.current
        const camera = cameraRef.current
        if (
            cameraPositionGroup == null ||
            cameraRotationGroup == null ||
            cameraLineGroup == null ||
            cameraWiggleGroup == null ||
            camera == null
        ) {
            return
        }

        activeAnimationRef.current?.stop()
        activeAnimationRef.current = null
        activePoseRef.current = cameraPoseRunning
        currentCameraLookAtRef.current.copy(cameraPoseRunning.cameraLookAt)
        currentRotatorRotationRef.current.copy(cameraPoseRunning.rotatorRotation)
        transitionStartRef.current.key = cameraPoseRunning.key
        transitionStartRef.current.rotatorPosition.copy(cameraPoseRunning.rotatorPosition)
        transitionStartRef.current.rotatorRotation.copy(cameraPoseRunning.rotatorRotation)
        transitionStartRef.current.cameraPosition.copy(cameraPoseRunning.cameraPosition)
        transitionStartRef.current.cameraLookAt.copy(cameraPoseRunning.cameraLookAt)
        transitionStartRef.current.transitionDuration = cameraPoseRunning.transitionDuration
        transitionStartRef.current.transitionEase = cameraPoseRunning.transitionEase
        transitionStartRef.current.fov = cameraPoseRunning.fov
        endCardTiltTargetRef.current.x = 0
        endCardTiltTargetRef.current.y = 0
        endCardTiltCurrentRef.current.x = 0
        endCardTiltCurrentRef.current.y = 0

        camera.fov = cameraPoseRunning.fov
        camera.updateProjectionMatrix()
        cameraRotationGroup.position.copy(cameraPoseRunning.rotatorPosition)
        cameraRotationGroup.rotation.set(
            cameraPoseRunning.rotatorRotation.x,
            cameraPoseRunning.rotatorRotation.y,
            cameraPoseRunning.rotatorRotation.z,
        )
        cameraLineGroup.position.setX(0)
        cameraWiggleGroup.rotation.set(0, 0, 0)
        cameraPositionGroup.position.copy(cameraPoseRunning.cameraPosition)
        cameraPositionGroup.quaternion.copy(
            createLocalLookAtQuaternion(
                cameraPoseRunning.cameraPosition,
                cameraPoseRunning.cameraLookAt,
            ),
        )
    }, [cameraPoseRunning, store.appFlowPhase])

    // End Card wiggle setup
    useEffect(() => {
        const isPriorityUiTarget = (target: EventTarget | null) =>
            target instanceof Element &&
            (target.closest('[data-ui-priority]') || target.closest('[data-endcard-ui]'))

        const updateTiltTarget = (clientX: number, clientY: number) => {
            if (
                runState.phase !== 'endCard' ||
                !runState.levelCompleteCardAnimationComplete ||
                endCardUiInteractionRef.current
            ) {
                clearTiltTarget()
                return
            }
            const normalizedX = (clientX / Math.max(size.width, 1)) * 2 - 1
            const normalizedY = (clientY / Math.max(size.height, 1)) * 2 - 1
            endCardTiltTargetRef.current.x = THREE.MathUtils.clamp(normalizedX, -1, 1)
            endCardTiltTargetRef.current.y = THREE.MathUtils.clamp(normalizedY, -1, 1)
        }

        const clearTiltTarget = () => {
            endCardTiltTargetRef.current.x = 0
            endCardTiltTargetRef.current.y = 0
        }

        const resetTiltTarget = () => {
            endCardUiInteractionRef.current = false
            clearTiltTarget()
        }

        const shouldSuppressEndCardTilt = (target: EventTarget | null) =>
            endCardUiInteractionRef.current || isPriorityUiTarget(target)

        const handlePointerDown = (event: PointerEvent) => {
            endCardUiInteractionRef.current = isPriorityUiTarget(event.target)
            if (endCardUiInteractionRef.current) {
                clearTiltTarget()
            }
        }

        const handleMouseMove = (event: PointerEvent) => {
            if (shouldSuppressEndCardTilt(event.target)) {
                clearTiltTarget()
                return
            }
            updateTiltTarget(event.clientX, event.clientY)
        }

        const handleTouchStart = (event: TouchEvent) => {
            endCardUiInteractionRef.current = isPriorityUiTarget(event.target)
            if (endCardUiInteractionRef.current) {
                clearTiltTarget()
            }
        }

        const handleTouchMove = (event: TouchEvent) => {
            const touch = event.touches[0]
            if (!touch) {
                clearTiltTarget()
                return
            }
            const target = document.elementFromPoint(touch.clientX, touch.clientY)
            if (shouldSuppressEndCardTilt(target)) {
                clearTiltTarget()
                return
            }
            updateTiltTarget(touch.clientX, touch.clientY)
        }

        window.addEventListener('pointerdown', handlePointerDown)
        window.addEventListener('pointermove', handleMouseMove)
        window.addEventListener('pointerleave', resetTiltTarget)
        window.addEventListener('pointerup', resetTiltTarget)
        window.addEventListener('pointercancel', resetTiltTarget)

        window.addEventListener('touchstart', handleTouchStart)
        window.addEventListener('touchmove', handleTouchMove)
        window.addEventListener('touchcancel', resetTiltTarget)
        window.addEventListener('touchend', resetTiltTarget)

        return () => {
            window.removeEventListener('pointerdown', handlePointerDown)
            window.removeEventListener('pointermove', handleMouseMove)
            window.removeEventListener('pointerleave', resetTiltTarget)
            window.removeEventListener('pointerup', resetTiltTarget)
            window.removeEventListener('pointercancel', resetTiltTarget)

            window.removeEventListener('touchstart', handleTouchStart)
            window.removeEventListener('touchmove', handleTouchMove)
            window.removeEventListener('touchcancel', resetTiltTarget)
            window.removeEventListener('touchend', resetTiltTarget)
        }
    }, [runState.phase, size.height, size.width])

    useEffect(() => {
        if (runState.phase === 'endCard') {
            return
        }

        endCardTiltTargetRef.current.x = 0
        endCardTiltTargetRef.current.y = 0
        endCardTiltCurrentRef.current.x = 0
        endCardTiltCurrentRef.current.y = 0
        cameraWiggleGroupRef.current?.rotation.set(0, 0, 0)
    }, [runState.phase])

    useFrame((_, _deltaSeconds) => {
        if (store.debugFreeFlyCameraEnabled) {
            return
        }

        const cameraPositionGroup = cameraPositionGroupRef.current
        const cameraRotationGroup = cameraRotationGroupRef.current
        const cameraLineGroup = cameraLineGroupRef.current
        const camera = cameraRef.current
        if (
            cameraPositionGroup == null ||
            cameraRotationGroup == null ||
            cameraLineGroup == null ||
            camera == null
        ) {
            return
        }

        // Select camera animation
        const currentCameraLookAt = currentCameraLookAtRef.current
        const currentRotatorRotation = currentRotatorRotationRef.current
        const transitionStart = transitionStartRef.current
        const appFlowPhase = store.appFlowPhase
        const targetPose: CameraPose = (() => {
            if (appFlowPhase === 'countdown') return cameraPoseRunning
            switch (runState.phase) {
                case 'idle':
                    switch (appFlowPhase) {
                        case 'start':
                            return cameraPoseStart
                        case 'intro':
                            return cameraPoseIntro
                        case 'configurator':
                            return cameraPoseConfigurator
                        default:
                            return cameraPoseStart
                    }
                case 'running':
                    return cameraPoseRunning
                case 'paused':
                    return cameraPoseRunning
                case 'levelTransition':
                    return cameraPoseRunning
                case 'gameOver':
                    return cameraPoseGameOver
                case 'levelComplete':
                    return cameraPoseCompleted
                case 'endCard':
                    return runState.getGameEndCardPressed
                        ? cameraPoseEndCardPressed
                        : cameraPoseEndCard
                default:
                    return cameraPoseStart
            }
        })()

        // Play camera animation
        if (activePoseRef.current !== targetPose) {
            activePoseRef.current = targetPose
            activeAnimationRef.current?.stop()
            transitionStart.fov = camera.fov
            transitionStart.rotatorPosition.copy(cameraRotationGroup.position)
            transitionStart.rotatorRotation.setFromEuler(cameraRotationGroup.rotation)
            transitionStart.cameraPosition.copy(cameraPositionGroup.position)
            transitionStart.cameraLookAt.copy(currentCameraLookAt)
            const controls = animate(0, 1, {
                duration: targetPose.transitionDuration,
                ease: targetPose.transitionEase,
                onUpdate: (latest) => {
                    // Update Camera Rotation
                    camera.fov = lerp(transitionStart.fov, targetPose.fov, latest)
                    camera.updateProjectionMatrix()

                    // Update the Camera Rotator
                    cameraRotationGroup.position.lerpVectors(
                        transitionStart.rotatorPosition,
                        targetPose.rotatorPosition,
                        latest,
                    )
                    currentRotatorRotation.lerpVectors(
                        transitionStart.rotatorRotation,
                        targetPose.rotatorRotation,
                        latest,
                    )
                    cameraRotationGroup.rotation.set(
                        currentRotatorRotation.x,
                        currentRotatorRotation.y,
                        currentRotatorRotation.z,
                    )

                    // Update the Line Position
                    if (targetPose.key === 'gameOver') {
                        const targetLineOffset = lanePositions[runState.snapshot.laneIndex + 1]
                        cameraLineGroup.position.setX(lerp(0, targetLineOffset, latest))
                    } else {
                        cameraLineGroup.position.setX(0.0)
                    }

                    // Update the Camera
                    cameraPositionGroup.position.lerpVectors(
                        transitionStart.cameraPosition,
                        targetPose.cameraPosition,
                        latest,
                    )
                    currentCameraLookAt.lerpVectors(
                        transitionStart.cameraLookAt,
                        targetPose.cameraLookAt,
                        latest,
                    )
                    cameraPositionGroup.quaternion.copy(
                        createLocalLookAtQuaternion(cameraPositionGroup.position, currentCameraLookAt)
                    )
                },
            })

            activeAnimationRef.current = controls
            controls.then(() => {
                if (activeAnimationRef.current !== controls) {
                    return
                }
                activeAnimationRef.current = null

                if (
                    targetPose === cameraPoseCompleted &&
                    runState.phase === 'levelComplete'
                ) {
                    runState.completeLevelComplete()
                }

                transitionStart.key = targetPose.key
                transitionStart.rotatorPosition.copy(targetPose.rotatorPosition)
                transitionStart.rotatorRotation.copy(targetPose.rotatorRotation)
                transitionStart.cameraPosition.copy(targetPose.cameraPosition)
                transitionStart.cameraLookAt.copy(targetPose.cameraLookAt)
                transitionStart.transitionDuration = targetPose.transitionDuration
                transitionStart.transitionEase = targetPose.transitionEase
            })
        }

        // hide overlay after transition is complete
        if (store.resetTransitionActive) {
            const cameraSettled =
                activeAnimationRef.current == null &&
                activePoseRef.current === targetPose
            const elapsed =
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) -
                store.resetTransitionStartedAt
            if (cameraSettled && elapsed >= RESET_TRANSITION_MIN_MS) {
                store.endResetTransition()
            }
        }

        // Intro Countdown 
        if (appFlowPhase === 'countdown') {
            const introComplete =
                activeAnimationRef.current == null &&
                activePoseRef.current === cameraPoseRunning

            if (
                introComplete &&
                store.countdownComplete &&
                runState.phase === 'idle'
            ) {
                store.dispatchAppFlow(
                    { type: 'CAMERA_INTRO_COMPLETE' },
                    { canContinueFromSplash: true },
                )
                runState.start()
            }
            return
        }

        // End Card Wiggle
        const endCardTiltTarget = endCardTiltTargetRef.current
        const endCardTiltCurrent = endCardTiltCurrentRef.current
        const endCardWiggleEnabled =
            runState.phase === 'endCard' &&
            runState.levelCompleteCardAnimationComplete
        const targetTiltX = endCardWiggleEnabled
            ? -endCardTiltTarget.y * endCardTiltVertical
            : 0

        const targetTiltY = endCardWiggleEnabled
            ? endCardTiltTarget.x * endCardTiltHorizontal
            : 0
        endCardTiltCurrent.x = lerp(endCardTiltCurrent.x, targetTiltX, endCardTiltSpeed)
        endCardTiltCurrent.y = lerp(endCardTiltCurrent.y, targetTiltY, endCardTiltSpeed)
        cameraWiggleGroupRef.current!.rotation.set(endCardTiltCurrent.x, -endCardTiltCurrent.y, 0)
    })
    return (
        <group ref={cameraRootGroupRef}>
            <group ref={cameraLineGroupRef}>
                <group ref={cameraRotationGroupRef}>
                    <group ref={cameraWiggleGroupRef}>
                        <group ref={cameraPositionGroupRef}>
                            <perspectiveCamera ref={cameraRef} />
                        </group>
                    </group>
                </group>
            </group>
        </group>
    )
}

function createLocalLookAtQuaternion(position: THREE.Vector3, lookAt: THREE.Vector3) {
    const matrix = new THREE.Matrix4()
    matrix.lookAt(position, lookAt, new THREE.Vector3(0, 1, 0))

    const quaternion = new THREE.Quaternion()
    quaternion.setFromRotationMatrix(matrix)
    return quaternion
}






